// Turns raw request/response byte arrays into plain-language summaries and a
// per-byte "breakdown" table — the core teaching feature. Decoding is driven
// entirely by the bytes themselves (the protocol is self-describing); `profile`
// is only consulted to enrich DID/DTC names, never to decide pass/fail.
import { SESSIONS, SERVICE_BY_SID, serviceName, nrcName, nrcMeaning, COMMON_DID_BY_ID, hex2 } from './constants';
import { dtcBytesToCode, decodeStatusByte } from './dtc';

const RESET_TYPES = { 0x01: 'Hard Reset', 0x02: 'Key Off/On Reset', 0x03: 'Soft Reset' };

function entry(range, hex, label, meaning) {
  return { range, hex, label, meaning };
}

function byteHex(bytes, i) {
  return i < bytes.length ? hex2(bytes[i]) : '--';
}

function findDid(profile, id) {
  const local = profile?.dids?.find((d) => d.id === id);
  return local || COMMON_DID_BY_ID[id] || null;
}

function decodeDidValue(bytes, didMeta) {
  if (!didMeta) return bytes.map(hex2).join(' ');
  if (didMeta.format === 'ascii') return String.fromCharCode(...bytes).replace(/\s+$/, '');
  return `0x${bytes.map(hex2).join('')}`;
}

function statusSummary(status) {
  const bits = decodeStatusByte(status);
  return bits.length ? bits.map((b) => b.name).join(', ') : 'no status bits set';
}

// Lightweight structured facts extracted from a request/response, used by
// scenario step-completion checks (scenarios.js) — kept separate from the
// human-text decode functions above so scenario logic never has to parse text.
export function extractRequestMeta(bytes) {
  const sid = bytes[0];
  const meta = { sid, subFn: bytes[1] };
  if (sid === 0x22 || sid === 0x2e) meta.did = (bytes[1] << 8) | bytes[2];
  if (sid === 0x19 && bytes[1] === 0x04) meta.dtcBytes = bytes.slice(2, 5);
  return meta;
}

export function extractResponseMeta(bytes, requestMeta) {
  if (bytes[0] === 0x7f) return { sid: bytes[1], positive: false, nrc: bytes[2], subFn: requestMeta?.subFn, did: requestMeta?.did };
  const sid = bytes[0] - 0x40;
  const meta = { sid, positive: true, subFn: bytes[1], did: requestMeta?.did };
  if (sid === 0x22) meta.did = (bytes[1] << 8) | bytes[2];
  return meta;
}

// --- Request decoding ------------------------------------------------------

export function decodeRequest(bytes, profile) {
  const sid = bytes[0];
  const svc = SERVICE_BY_SID[sid];
  const breakdown = [entry('Byte 0', byteHex(bytes, 0), 'Service ID (SID)', svc ? `${svc.name} — request` : 'Unrecognized service')];

  if (!svc) {
    return { summary: `Request: unrecognized Service ID 0x${hex2(sid)}`, breakdown };
  }

  switch (sid) {
    case 0x10: {
      const session = SESSIONS[bytes[1]];
      breakdown.push(entry('Byte 1', byteHex(bytes, 1), 'Sub-function', session ? session.name : 'Unknown session type'));
      return { summary: `Request: Diagnostic Session Control → ${session ? session.name : `unknown (0x${byteHex(bytes, 1)})`}`, breakdown };
    }
    case 0x11: {
      const name = RESET_TYPES[bytes[1]] || `Unknown reset type (0x${byteHex(bytes, 1)})`;
      breakdown.push(entry('Byte 1', byteHex(bytes, 1), 'Sub-function', name));
      return { summary: `Request: ECU Reset → ${name}`, breakdown };
    }
    case 0x14: {
      breakdown.push(entry('Bytes 1-3', bytes.slice(1, 4).map(hex2).join(' '), 'Group of DTC', 'FF FF FF = all DTCs'));
      return { summary: 'Request: Clear Diagnostic Information (all DTCs)', breakdown };
    }
    case 0x19: {
      const subFn = bytes[1];
      breakdown.push(entry('Byte 1', byteHex(bytes, 1), 'Sub-function', subFn === 0x02 ? 'reportDTCByStatusMask' : subFn === 0x04 ? 'reportDTCSnapshotRecordByDTCNumber' : 'Unsupported sub-function'));
      if (subFn === 0x02) {
        breakdown.push(entry('Byte 2', byteHex(bytes, 2), 'DTC Status Mask', statusSummary(bytes[2] ?? 0)));
        return { summary: `Request: Read DTC Information → status mask 0x${byteHex(bytes, 2)} (${statusSummary(bytes[2] ?? 0)})`, breakdown };
      }
      if (subFn === 0x04) {
        const dtcBytes = bytes.slice(2, 5);
        breakdown.push(entry('Bytes 2-4', dtcBytes.map(hex2).join(' '), 'DTC', dtcBytes.length === 3 ? dtcBytesToCode(dtcBytes) : 'incomplete'));
        breakdown.push(entry('Byte 5', byteHex(bytes, 5), 'Snapshot Record Number', String(bytes[5] ?? '')));
        return { summary: `Request: Read DTC Snapshot for ${dtcBytes.length === 3 ? dtcBytesToCode(dtcBytes) : '?'}`, breakdown };
      }
      return { summary: 'Request: Read DTC Information (unsupported sub-function)', breakdown };
    }
    case 0x22: {
      const did = (bytes[1] << 8) | bytes[2];
      const meta = findDid(profile, did);
      breakdown.push(entry('Bytes 1-2', bytes.slice(1, 3).map(hex2).join(' '), 'Data Identifier (DID)', meta ? meta.name : 'Unknown DID'));
      return { summary: `Request: Read Data By Identifier → 0x${hex2(bytes[1])}${hex2(bytes[2])} (${meta ? meta.name : 'unknown'})`, breakdown };
    }
    case 0x27: {
      const subFn = bytes[1];
      breakdown.push(entry('Byte 1', byteHex(bytes, 1), 'Sub-function', subFn === 0x01 ? 'requestSeed' : subFn === 0x02 ? 'sendKey' : 'Unknown'));
      if (subFn === 0x02) breakdown.push(entry('Bytes 2-3', bytes.slice(2, 4).map(hex2).join(' '), 'Key', 'Computed from the seed using the documented algorithm'));
      return { summary: `Request: Security Access → ${subFn === 0x01 ? 'request seed' : subFn === 0x02 ? 'send key' : 'unknown'}`, breakdown };
    }
    case 0x2e: {
      const did = (bytes[1] << 8) | bytes[2];
      const meta = findDid(profile, did);
      const data = bytes.slice(3);
      breakdown.push(entry('Bytes 1-2', bytes.slice(1, 3).map(hex2).join(' '), 'Data Identifier (DID)', meta ? meta.name : 'Unknown DID'));
      breakdown.push(entry(`Bytes 3-${bytes.length - 1}`, data.map(hex2).join(' '), 'Data to Write', meta ? decodeDidValue(data, meta) : data.map(hex2).join(' ')));
      return { summary: `Request: Write Data By Identifier → 0x${hex2(bytes[1])}${hex2(bytes[2])} (${meta ? meta.name : 'unknown'})`, breakdown };
    }
    case 0x3e: {
      const suppress = (bytes[1] & 0x80) !== 0;
      breakdown.push(entry('Byte 1', byteHex(bytes, 1), 'Sub-function', `zeroSubFunction${suppress ? ' + suppressPosRspMsgIndicationBit' : ''}`));
      return { summary: `Request: Tester Present${suppress ? ' (response suppressed)' : ''}`, breakdown };
    }
    default:
      return { summary: `Request: ${serviceName(sid)}`, breakdown };
  }
}

// --- Response decoding -------------------------------------------------

export function decodeResponse(bytes, requestBytes, profile) {
  if (bytes[0] === 0x7f) {
    const sid = bytes[1];
    const nrc = bytes[2];
    const breakdown = [
      entry('Byte 0', byteHex(bytes, 0), 'Negative Response Marker', '0x7F indicates a negative response'),
      entry('Byte 1', byteHex(bytes, 1), 'Original Service ID', serviceName(sid)),
      entry('Byte 2', byteHex(bytes, 2), 'Negative Response Code (NRC)', `${nrcName(nrc)} — ${nrcMeaning(nrc)}`),
    ];
    return {
      positive: false,
      summary: `Negative Response: ${serviceName(sid)} failed — ${nrcName(nrc)} (0x${hex2(nrc)})`,
      detail: nrcMeaning(nrc),
      breakdown,
    };
  }

  const sid = bytes[0] - 0x40;
  const breakdown = [entry('Byte 0', byteHex(bytes, 0), 'Response SID', `${serviceName(sid)} — positive response (request SID + 0x40)`)];

  switch (sid) {
    case 0x10: {
      const session = SESSIONS[bytes[1]];
      breakdown.push(entry('Byte 1', byteHex(bytes, 1), 'Sub-function (echo)', session ? session.name : 'unknown'));
      breakdown.push(entry('Bytes 2-5', bytes.slice(2, 6).map(hex2).join(' '), 'P2 / P2* Timing', 'Illustrative session timing parameters (max response time, enhanced max response time)'));
      return { positive: true, summary: `ECU is now in ${session ? session.name : 'an unknown session'}.`, breakdown };
    }
    case 0x11: {
      const name = RESET_TYPES[bytes[1]] || 'unknown';
      breakdown.push(entry('Byte 1', byteHex(bytes, 1), 'Sub-function (echo)', name));
      return { positive: true, summary: `ECU acknowledged ${name} — session reset to Default, security re-locked.`, breakdown };
    }
    case 0x14: {
      return { positive: true, summary: 'DTC memory cleared — all stored fault status bits reset to 0x00.', breakdown };
    }
    case 0x19: {
      const subFn = bytes[1];
      breakdown.push(entry('Byte 1', byteHex(bytes, 1), 'Sub-function (echo)', subFn === 0x02 ? 'reportDTCByStatusMask' : 'reportDTCSnapshotRecordByDTCNumber'));
      if (subFn === 0x02) {
        breakdown.push(entry('Byte 2', byteHex(bytes, 2), 'DTC Availability Mask', statusSummary(bytes[2] ?? 0)));
        const dtcs = [];
        for (let i = 3; i + 4 <= bytes.length; i += 4) {
          const dtcBytes = bytes.slice(i, i + 3);
          const status = bytes[i + 3];
          const code = dtcBytesToCode(dtcBytes);
          dtcs.push({ code, status });
          breakdown.push(entry(`Bytes ${i}-${i + 3}`, [...dtcBytes, status].map(hex2).join(' '), `DTC ${code}`, `Status 0x${hex2(status)}: ${statusSummary(status)}`));
        }
        return {
          positive: true,
          summary: dtcs.length ? `${dtcs.length} DTC(s) matched the status mask: ${dtcs.map((d) => d.code).join(', ')}` : 'No DTCs matched the requested status mask.',
          breakdown,
        };
      }
      // subFn 0x04 — snapshot
      const dtcBytes = bytes.slice(2, 5);
      const status = bytes[5];
      const record = bytes[6];
      const did = (bytes[8] << 8) | bytes[9];
      const data = bytes.slice(10);
      const meta = findDid(profile, did);
      const code = dtcBytesToCode(dtcBytes);
      breakdown.push(
        entry('Bytes 2-4', dtcBytes.map(hex2).join(' '), 'DTC', code),
        entry('Byte 5', byteHex(bytes, 5), 'DTC Status', statusSummary(status ?? 0)),
        entry('Byte 6', byteHex(bytes, 6), 'Snapshot Record Number', String(record ?? '')),
        entry('Byte 7', byteHex(bytes, 7), 'Number of Data Identifiers', String(bytes[7] ?? '')),
        entry('Bytes 8-9', bytes.slice(8, 10).map(hex2).join(' '), 'Snapshot DID', meta ? meta.name : 'unknown'),
        entry(`Bytes 10-${bytes.length - 1}`, data.map(hex2).join(' '), 'Snapshot Value', meta ? decodeDidValue(data, meta) : data.map(hex2).join(' ')),
      );
      return { positive: true, summary: `Snapshot for ${code}: ${meta ? meta.name : 'value'} = ${meta ? decodeDidValue(data, meta) : data.map(hex2).join(' ')}`, breakdown };
    }
    case 0x22: {
      const did = (bytes[1] << 8) | bytes[2];
      const meta = findDid(profile, did);
      const data = bytes.slice(3);
      const value = decodeDidValue(data, meta);
      breakdown.push(
        entry('Bytes 1-2', bytes.slice(1, 3).map(hex2).join(' '), 'Data Identifier (echo)', meta ? meta.name : 'unknown'),
        entry(`Bytes 3-${bytes.length - 1}`, data.map(hex2).join(' '), 'Data Value', value),
      );
      return { positive: true, summary: `${meta ? meta.name : `DID 0x${hex2(bytes[1])}${hex2(bytes[2])}`} = "${value}"`, breakdown };
    }
    case 0x27: {
      const subFn = bytes[1];
      breakdown.push(entry('Byte 1', byteHex(bytes, 1), 'Sub-function (echo)', subFn === 0x01 ? 'requestSeed' : 'sendKey'));
      if (subFn === 0x01) {
        const seed = bytes.slice(2, 4);
        const alreadyUnlocked = seed[0] === 0 && seed[1] === 0;
        breakdown.push(entry('Bytes 2-3', seed.map(hex2).join(' '), 'Seed', alreadyUnlocked ? 'All-zero seed — security is already unlocked' : 'Compute the key from this seed using the documented algorithm'));
        return { positive: true, summary: alreadyUnlocked ? 'Security is already unlocked (seed 00 00).' : `Seed issued: 0x${seed.map(hex2).join('')}`, breakdown };
      }
      return { positive: true, summary: 'Key accepted — security is now unlocked.', breakdown };
    }
    case 0x2e: {
      const did = (bytes[1] << 8) | bytes[2];
      const meta = findDid(profile, did);
      breakdown.push(entry('Bytes 1-2', bytes.slice(1, 3).map(hex2).join(' '), 'Data Identifier (echo)', meta ? meta.name : 'unknown'));
      return { positive: true, summary: `Write accepted for ${meta ? meta.name : `DID 0x${hex2(bytes[1])}${hex2(bytes[2])}`}.`, breakdown };
    }
    case 0x3e: {
      breakdown.push(entry('Byte 1', byteHex(bytes, 1), 'Sub-function (echo)', 'zeroSubFunction'));
      return { positive: true, summary: 'Tester Present acknowledged — S3 session timer reset.', breakdown };
    }
    default:
      return { positive: true, summary: `Positive response for ${serviceName(sid)}.`, breakdown };
  }
}
